import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation
} from 'react-router-dom';
import axios from 'axios';

// Use relative URL in development (leverages React proxy), full URL in production
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Jednoduché SVG ikony - technický štýl
const HomeIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

const SmartToyIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
  </svg>
);

const GraphicEqIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
  </svg>
);

const ContactMailIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const SettingsIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const MenuIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

const CloseIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

function Navigation({ mobileOpen, setMobileOpen }) {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navItems = [
    { label: 'Domov', path: '/', icon: <HomeIcon /> },
    { label: 'Autonómne stroje', path: '/autonomous', icon: <SmartToyIcon /> },
    { label: 'Audio mixing', path: '/audio', icon: <GraphicEqIcon /> },
    { label: 'Technické riešenia', path: '/technical', icon: <SettingsIcon /> },
    { label: 'Služby', path: '/services', icon: <ContactMailIcon /> },
  ];

  if (isMobile) {
    return (
      <>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden p-3 rounded-xl bg-gradient-to-r from-ai-purple to-ai-blue text-white hover:from-ai-purple-dark hover:to-ai-blue-dark transition-all transform hover:scale-105 shadow-ai-md"
          aria-label="Otvoriť menu"
        >
          {mobileOpen ? <CloseIcon /> : <MenuIcon />}
        </button>
        
        {mobileOpen && (
          <div className="absolute top-16 left-0 right-0 bg-white/95 backdrop-blur-lg border-t-2 border-b-2 border-ai-purple/30 shadow-ai-lg z-50 rounded-b-3xl">
            <nav className="flex flex-col">
              {navItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-6 py-4 border-b border-ai-gray-200 hover:bg-gradient-to-r hover:from-ai-purple/10 hover:to-ai-cyan/10 transition-all ${
                    location.pathname === item.path 
                      ? 'bg-gradient-to-r from-ai-purple to-ai-blue text-white font-bold shadow-ai-md' 
                      : 'text-ai-gray-800'
                  }`}
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  <span>{item.label}</span>
                </Link>
              ))}
            </nav>
          </div>
        )}
      </>
    );
  }

  return (
    <nav className="hidden md:flex items-center gap-2 flex-1">
      {navItems.map((item) => (
        <Link
          key={item.label}
          to={item.path}
          className={`flex items-center gap-2 px-5 py-3 rounded-xl transition-all font-semibold text-sm transform hover:scale-105 ${
            location.pathname === item.path
              ? 'bg-gradient-to-r from-ai-purple to-ai-blue text-white shadow-ai-md'
              : 'text-ai-gray-700 hover:bg-gradient-to-r hover:from-ai-purple/20 hover:to-ai-cyan/20 hover:text-ai-purple'
          }`}
        >
          {item.icon}
          {item.label}
        </Link>
      ))}
    </nav>
  );
}

function Home() {
  return (
    <div>
      {/* Hero sekcia - moderný AI dizajn */}
      <section className="relative bg-gradient-to-br from-ai-purple via-ai-blue to-ai-cyan text-white py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-ai-purple/20 via-ai-blue/20 to-ai-cyan/20 animate-pulse"></div>
        <div className="container mx-auto px-4 max-w-6xl relative z-10">
          <div className="text-center float-animation">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white drop-shadow-lg">
              Inteligentné stroje a audio systémy
            </h1>
            <h2 className="text-2xl md:text-3xl font-bold mb-8 tracking-wide relative z-10" style={{ 
              color: '#ffffff',
              textShadow: '1px 1px 3px rgba(0,0,0,0.8)',
              fontWeight: '700',
              WebkitFontSmoothing: 'subpixel-antialiased',
              MozOsxFontSmoothing: 'auto'
            }}>
              AI riešenia pre autonómne stroje a profesionálne audio
            </h2>
            <p className="text-lg md:text-xl max-w-4xl mx-auto text-white leading-relaxed mb-10 drop-shadow-md">
              Automatizujte svoje procesy s pomocou umelej inteligencie. 
              Vytvoríme riešenie presne podľa vašich potrieb.
            </p>
            <a
              href="https://it-dk.sk/#kontakt"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-tech-secondary inline-block"
            >
              Kontaktovať
            </a>
          </div>
        </div>
      </section>

      {/* Hlavné sekcie */}
      <section className="container mx-auto px-4 py-16 max-w-6xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {/* Autonómne systémy */}
          <div className="card-tech flex flex-col h-full">
            <div className="mb-6">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-ai-purple to-ai-pink flex items-center justify-center mb-4 shadow-neon-purple float-animation">
                <SmartToyIcon className="text-white" />
              </div>
              <h3 className="text-3xl font-bold mb-4 text-ai-purple drop-shadow-sm">
                Autonómne stroje
              </h3>
            </div>
            <p className="text-base text-ai-gray-800 mb-4 leading-relaxed">
              Stroje a roboty, ktoré sa riadia sami. Pre vozidlá, roboty, drony a automatizáciu skladov.
            </p>
            <p className="text-sm text-ai-gray-800 mb-6 leading-relaxed">
              <strong className="text-ai-purple">Použitie:</strong> Autonómne vozidlá, skladové roboty, 
              priemyselné roboty, drony, automatizácia skladov
            </p>
            <div className="mt-auto">
              <Link
                to="/autonomous"
                className="btn-tech-primary inline-block"
              >
                Zistiť viac
              </Link>
            </div>
          </div>

          {/* Audio Mixing */}
          <div className="card-tech flex flex-col h-full">
            <div className="mb-6">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-ai-cyan to-ai-blue flex items-center justify-center mb-4 shadow-neon-cyan float-animation">
                <GraphicEqIcon className="text-white" />
              </div>
              <h3 className="text-3xl font-bold mb-4 text-ai-purple drop-shadow-sm">
                Audio spracovanie
              </h3>
            </div>
            <p className="text-base text-ai-gray-800 mb-4 leading-relaxed">
              Automatické mixovanie a úprava zvuku pomocou AI. Pre hudbu, podcasty, živé vystúpenia a nahrávky.
            </p>
            <p className="text-sm text-ai-gray-800 mb-6 leading-relaxed">
              <strong className="text-ai-cyan">Použitie:</strong> Mixovanie hudby, živé vystúpenia, 
              mastering, audio efekty, vylepšenie hlasu
            </p>
            <div className="mt-auto">
              <Link
                to="/audio"
                className="btn-tech-secondary inline-block"
              >
                Zistiť viac
              </Link>
            </div>
          </div>
        </div>

        {/* Prečo si vybrať */}
        <div className="text-center">
          <h2 className="section-header mb-12">Prečo si vybrať nás?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="border-2 border-ai-purple/30 p-8 rounded-3xl bg-gradient-to-br from-white to-ai-purple/10 hover:shadow-ai-lg transition-all transform hover:scale-105">
              <div className="text-3xl font-bold mb-3 gradient-text">✓ Úspora času</div>
              <p className="text-sm text-ai-gray-800 leading-relaxed">
                Menej manuálnej práce, viac automatizácie
              </p>
            </div>
            <div className="border-2 border-ai-cyan/30 p-8 rounded-3xl bg-gradient-to-br from-white to-ai-cyan/10 hover:shadow-ai-lg transition-all transform hover:scale-105">
              <div className="text-3xl font-bold mb-3 gradient-text">✓ Presné výsledky</div>
              <p className="text-sm text-ai-gray-800 leading-relaxed">
                AI pracuje konzistentne a presne
              </p>
            </div>
            <div className="border-2 border-ai-blue/30 p-8 rounded-3xl bg-gradient-to-br from-white to-ai-blue/10 hover:shadow-ai-lg transition-all transform hover:scale-105">
              <div className="text-3xl font-bold mb-3 gradient-text">✓ Rast s vami</div>
              <p className="text-sm text-ai-gray-800 leading-relaxed">
                Riešenie sa prispôsobí vašim potrebám
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function SolutionsPage({ type }) {
  const [solutions, setSolutions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSolutions = async () => {
      try {
        const endpoint = type === 'autonomous' 
          ? `${API_BASE_URL}/solutions/autonomous`
          : `${API_BASE_URL}/solutions/audio`;
        const response = await axios.get(endpoint);
        setSolutions(response.data || []);
      } catch (error) {
        console.error('Error fetching solutions:', error);
        setSolutions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSolutions();
  }, [type]);

  const title = type === 'autonomous' 
    ? 'AI Riešenia pre Autonómne Stroje'
    : 'AI Riešenia pre Audio Mixing';
  
  const description = type === 'autonomous'
    ? 'Komplexné AI riešenia pre autonómne riadenie strojov a robotov'
    : 'Profesionálne AI nástroje pre audio mixovanie a spracovanie zvuku';

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16 max-w-6xl text-center">
        <p className="text-lg text-ai-gray-800">Načítavanie...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header sekcia */}
      <section className={`relative bg-gradient-to-br from-ai-purple via-ai-blue to-ai-cyan text-white py-20 overflow-hidden ${
        type === 'autonomous' ? 'border-ai-cyan' : 'border-ai-cyan'
      }`}>
        <div className="absolute inset-0 bg-gradient-to-br from-ai-purple/20 via-ai-blue/20 to-ai-cyan/20 animate-pulse"></div>
        <div className="container mx-auto px-4 max-w-6xl text-center relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold mb-4 text-white drop-shadow-lg">
            {title}
          </h1>
          <p className="text-xl md:text-2xl font-semibold text-white tracking-wide" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.7)', WebkitFontSmoothing: 'subpixel-antialiased', MozOsxFontSmoothing: 'auto' }}>
            {description}
          </p>
        </div>
      </section>

      {/* Riešenia */}
      <section className="container mx-auto px-4 py-16 max-w-6xl">
        {!solutions || solutions.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-lg text-ai-gray-800">Žiadne riešenia sa nenašli.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution) => (
              <div key={solution.id} className="card-tech flex flex-col h-full">
                {solution.imageUrl && (
                  <img
                    src={solution.imageUrl}
                    alt={solution.title || 'Solution image'}
                    className="w-full h-48 object-cover border-b-2 border-ai-gray-300 rounded-t-3xl"
                  />
                )}
                <div className="flex-1 p-6 flex flex-col">
                  <h3 className="text-xl font-bold mb-3 uppercase tracking-tight">
                    {solution.title}
                  </h3>
                  <p className="text-sm text-ai-gray-800 mb-4 leading-relaxed">
                    {solution.description}
                  </p>
                  
                  {solution.applications && solution.applications.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-bold uppercase tracking-wider text-ai-gray-900 mb-2">
                        Aplikácie:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {solution.applications.map((app, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1.5 text-xs border-2 border-ai-purple/30 text-ai-gray-800 uppercase tracking-wide rounded-xl bg-gradient-to-r from-ai-purple/10 to-ai-cyan/10 hover:from-ai-purple/20 hover:to-ai-cyan/20 transition-all"
                          >
                            {app}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {solution.benefits && solution.benefits.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-bold uppercase tracking-wider text-ai-gray-900 mb-2">
                        Výhody:
                      </p>
                      <ul className="list-none space-y-1">
                        {solution.benefits.map((benefit, idx) => (
                          <li key={idx} className="text-sm text-ai-gray-800 leading-relaxed">
                            • {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {solution.practicalExamples && solution.practicalExamples.length > 0 && (
                      <div className="mt-4 pt-4 border-t-2 border-ai-gray-300">
                      <p className="text-xs font-bold uppercase tracking-wider text-ai-gray-900 mb-4 flex items-center gap-2">
                        <span className="text-base">💡</span>
                        <span>Praktické príklady:</span>
                      </p>
                      <div className="space-y-4">
                        {solution.practicalExamples.map((example, idx) => (
                          <div key={idx} className="bg-gradient-to-br from-ai-purple/10 to-ai-cyan/10 p-5 border-2 border-ai-purple/30 rounded-2xl hover:shadow-ai-md hover:scale-[1.02] transition-all relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-ai-purple/5 to-ai-cyan/5 rounded-full -mr-10 -mt-10"></div>
                            <div className="relative z-10">
                              <h4 className="text-base font-bold text-ai-gray-900 mb-3 uppercase tracking-tight flex items-start gap-2">
                                <span className="text-lg font-bold text-ai-purple">{idx + 1}.</span>
                                <span>{example.title}</span>
                              </h4>
                              <p className="text-sm text-ai-gray-800 leading-relaxed mb-4 pl-6">
                                {example.description}
                              </p>
                              {example.benefits && example.benefits.length > 0 && (
                                <div className="flex flex-wrap gap-2 pl-6">
                                  {example.benefits.map((benefit, bidx) => (
                                    <span
                                      key={bidx}
                                      className="px-3 py-1.5 text-xs border-2 border-ai-cyan/50 bg-white/80 text-ai-purple uppercase tracking-wide font-semibold rounded-lg shadow-sm hover:shadow-md transition-shadow"
                                    >
                                      ✓ {benefit}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function TechnicalSolutionsPage() {
  const [technicalSolutions, setTechnicalSolutions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTechnicalSolutions = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/technical-solutions`);
        setTechnicalSolutions(response.data || null);
      } catch (error) {
        console.error('Error fetching technical solutions:', error);
        setTechnicalSolutions(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTechnicalSolutions();
  }, []);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16 max-w-6xl text-center">
        <p className="text-lg text-ai-gray-800">Načítavanie...</p>
      </div>
    );
  }

  if (!technicalSolutions) {
    return (
      <div className="container mx-auto px-4 py-16 max-w-6xl text-center">
        <p className="text-lg text-ai-gray-800">Technické riešenia sa nenašli.</p>
      </div>
    );
  }

  const sections = [
    { key: 'customModels', data: technicalSolutions.customModels },
    { key: 'hybridSolutions', data: technicalSolutions.hybridSolutions },
    { key: 'vectorDatabase', data: technicalSolutions.vectorDatabase },
    { key: 'adaptiveSolutions', data: technicalSolutions.adaptiveSolutions },
  ];

  return (
    <div>
      {/* Header sekcia */}
      <section className="relative bg-gradient-to-br from-ai-purple via-ai-blue to-ai-cyan text-white py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-ai-purple/20 via-ai-blue/20 to-ai-cyan/20 animate-pulse"></div>
        <div className="container mx-auto px-4 max-w-6xl text-center relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold mb-4 text-white drop-shadow-lg">
            Technické riešenia
          </h1>
          <p className="text-xl md:text-2xl font-semibold text-white/90 tracking-wide">
            Flexibilné AI architektúry a technológie pre optimálne výsledky
          </p>
        </div>
      </section>

      {/* Technické riešenia */}
      <section className="container mx-auto px-4 py-16 max-w-6xl">
        <div className="space-y-16">
          {sections.map((section) => {
            const { data } = section;
            if (!data) return null;

            return (
              <div key={section.key} className="card-tech">
                <div className="mb-6">
                  <h2 className="text-3xl font-bold mb-4 uppercase tracking-tight border-b-4 border-ai-purple/30 pb-3">
                    {data.title}
                  </h2>
                  <p className="text-base text-ai-gray-800 leading-relaxed mb-6">
                    {data.description}
                  </p>
                </div>

                {data.useCases && data.useCases.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {data.useCases.map((useCase, idx) => (
                      <div
                        key={idx}
                        className="bg-gradient-to-br from-ai-purple/10 to-ai-cyan/10 p-6 border-2 border-ai-purple/30 rounded-2xl flex flex-col h-full hover:shadow-ai-md hover:scale-[1.02] transition-all"
                      >
                        <h3 className="text-lg font-bold text-ai-gray-900 mb-3 uppercase tracking-tight">
                          {useCase.title}
                        </h3>
                        <p className="text-sm text-ai-gray-800 leading-relaxed mb-4 flex-1">
                          {useCase.description}
                        </p>
                        {useCase.benefits && useCase.benefits.length > 0 && (
                          <div className="mt-auto">
                            <div className="flex flex-wrap gap-2">
                              {useCase.benefits.map((benefit, bidx) => (
                                <span
                                  key={bidx}
                                  className="px-2 py-1 text-xs border-2 border-ai-cyan text-ai-purple uppercase tracking-wide font-semibold"
                                >
                                  {benefit}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Kontaktný box */}
        <div className="bg-gradient-to-br from-ai-purple via-ai-blue to-ai-cyan text-white border-4 border-ai-cyan/50 rounded-3xl p-8 md:p-12 text-center mt-16 shadow-ai-xl relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
          <h3 className="text-2xl md:text-3xl font-bold mb-4 text-white relative z-10" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.7)', WebkitFontSmoothing: 'subpixel-antialiased', MozOsxFontSmoothing: 'auto' }}>
            Potrebujete špecifické technické riešenie?
          </h3>
          <p className="text-base md:text-lg mb-8 text-white leading-relaxed max-w-2xl mx-auto relative z-10" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.7)', WebkitFontSmoothing: 'subpixel-antialiased', MozOsxFontSmoothing: 'auto' }}>
            Každý projekt je jedinečný. Navrhneme a implementujeme technické riešenie presne podľa vašich požiadaviek, dát a situácie.
          </p>
          <a
            href="https://it-dk.sk/#kontakt"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-tech-secondary inline-block"
          >
            Kontaktovať
          </a>
        </div>
      </section>
    </div>
  );
}

function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/services`);
        setServices(response.data || []);
      } catch (error) {
        console.error('Error fetching services:', error);
        setServices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16 max-w-6xl text-center">
        <p className="text-lg text-ai-gray-800">Načítavanie...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header sekcia */}
      <section className="relative bg-gradient-to-br from-ai-purple via-ai-blue to-ai-cyan text-white py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-ai-purple/20 via-ai-blue/20 to-ai-cyan/20 animate-pulse"></div>
        <div className="container mx-auto px-4 max-w-6xl text-center relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold mb-4 text-white drop-shadow-lg">
            Naše Služby
          </h1>
          <p className="text-xl md:text-2xl font-semibold text-white/90 tracking-wide">
            Komplexné AI riešenia pre vaše podnikanie
          </p>
        </div>
      </section>

      {/* Služby */}
      <section className="container mx-auto px-4 py-16 max-w-6xl">
        {!services || services.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-lg text-ai-gray-800">Žiadne služby sa nenašli.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {services.map((service) => (
              <div key={service.id} className="card-tech">
                <h3 className="text-2xl font-bold mb-4 uppercase tracking-tight">
                  {service.title}
                </h3>
                <p className="text-base text-ai-gray-800 mb-6 leading-relaxed">
                  {service.description}
                </p>
                {service.price && (
                  <div className="inline-block px-6 py-3 border-2 border-ai-purple/30 text-ai-purple font-bold uppercase tracking-wider text-sm rounded-2xl bg-gradient-to-r from-ai-purple/10 to-ai-cyan/10 hover:shadow-ai-md transition-all transform hover:scale-105">
                    {service.price}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Kontaktný box */}
        <div className="bg-gradient-to-br from-ai-purple via-ai-blue to-ai-cyan text-white border-4 border-ai-cyan/50 rounded-3xl p-8 md:p-12 text-center shadow-ai-xl relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
          <div className="relative z-10">
            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-white" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.7)', WebkitFontSmoothing: 'subpixel-antialiased', MozOsxFontSmoothing: 'auto' }}>
              Zaujíma vás naša ponuka?
            </h3>
            <p className="text-base md:text-lg mb-8 text-white leading-relaxed max-w-2xl mx-auto" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.7)', WebkitFontSmoothing: 'subpixel-antialiased', MozOsxFontSmoothing: 'auto' }}>
              Kontaktujte nás pre bezplatnú konzultáciu a návrh riešenia šitého na mieru vašim potrebám.
            </p>
            <a
              href="https://it-dk.sk/#kontakt"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-tech-secondary inline-block"
            >
              Kontaktovať
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function App() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <Router>
      <div className="flex flex-col min-h-screen bg-white/90 backdrop-blur-md">
        {/* Header - moderný AI dizajn */}
        <header className="bg-white/95 backdrop-blur-lg border-b-2 border-ai-purple/30 sticky top-0 z-40 shadow-ai-sm">
          <div className="container mx-auto px-4 md:px-8 max-w-6xl">
            <div className="flex items-center justify-between h-20 gap-6">
              <Link
                to="/"
                className="text-2xl font-bold gradient-text hover:scale-105 transition-transform"
              >
                AI Riešenia
              </Link>
              <Navigation mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/autonomous" element={<SolutionsPage type="autonomous" />} />
            <Route path="/audio" element={<SolutionsPage type="audio" />} />
            <Route path="/technical" element={<TechnicalSolutionsPage />} />
            <Route path="/services" element={<ServicesPage />} />
          </Routes>
        </main>

        {/* Footer - moderný */}
        <footer className="bg-gradient-to-r from-ai-purple/10 via-ai-blue/10 to-ai-cyan/10 border-t-2 border-ai-purple/20 py-10 mt-auto">
          <div className="container mx-auto px-4 max-w-6xl text-center">
            <p className="text-sm text-ai-gray-700 uppercase tracking-wide mb-2 font-semibold">
              © {new Date().getFullYear()} AI Riešenia. Všetky práva vyhradené.
            </p>
            <p className="text-sm text-ai-gray-700">
              <a 
                href="https://it-dk.sk" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-ai-purple hover:text-ai-cyan hover:underline transition-colors font-semibold"
              >
                it-dk.sk
              </a>
            </p>
          </div>
        </footer>
      </div>
    </Router>
  );
}

export default App;
